import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";

import { resolveRestingComposerControlsLayout } from "../composerFooterLayout";
import { measureRestingComposerControls } from "./restingComposerControlsMeasurement";

/**
 * Whether the relocated controls fit the strip's portal host.
 *
 * The host takes whatever the Environment, worktree and branch controls leave
 * over, and that is a different number on every window width and every branch
 * name. Rendering into it regardless would clip the model picker against the
 * branch, so the cluster hides rather than overlaps, and returns the moment the
 * Composer is focused again.
 *
 * The measurement is deliberately taken from the controls' own natural widths
 * rather than from what is currently on screen. Judging against the visible
 * width lets the strip expand into the space the Composer just gave up, which
 * shrinks the host, hides the controls, frees the space again, and repeats;
 * `resolveRestingComposerControlsLayout` carries a pixel of slack for the same
 * reason.
 */
export function useRestingComposerControlsLayout(host: HTMLElement | null): {
  readonly controlsRef: RefObject<HTMLDivElement | null>;
  readonly visible: boolean;
} {
  const controlsRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef(host);
  hostRef.current = host;
  const [visible, setVisible] = useState(true);

  const measure = useCallback(() => {
    const currentHost = hostRef.current;
    const controls = controlsRef.current;
    // The controls only mount while the Composer rests, so an Expanded
    // Composer pays no layout reads here despite the every-render effect.
    if (currentHost === null || !controls) return;
    const measurement = measureRestingComposerControls(controls);
    if (!measurement) return;
    const hostWidth = currentHost.clientWidth;
    setVisible((current) => {
      const next = resolveRestingComposerControlsLayout({
        ...measurement,
        hostWidth,
        // No per-block overflow in this card: Forge renders the footer's
        // leading cluster as one component, so there are no measurable blocks
        // to fold away yet. Splitting it belongs with the Compact footer work
        // that owns the overflow menu.
        blockWidths: [],
        previous: { hiddenCount: 0, visible: current },
      });
      return next.visible === current ? current : next.visible;
    });
  }, []);

  useLayoutEffect(measure);

  useEffect(() => {
    if (!host || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    // A label that reflows when its font finishes loading changes the width
    // this decision was made on, and nothing else would tell us.
    document.fonts?.addEventListener("loadingdone", measure);
    return () => {
      observer.disconnect();
      document.fonts?.removeEventListener("loadingdone", measure);
    };
  }, [host, measure]);

  return { controlsRef, visible };
}
